<template>
  <div :class="controlClasses" class="mds-form-control mds-input-number">
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <!-- Верх: кнопки, иконка, input, unit, очистка (абсолютом поверх) -->
      <div class="control-row">
        <button
          @click="decrease"
          class="control-btn decrease"
          type="button"
          v-if="showButtons"
        >
          <VIcon material="remove" />
        </button>

        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <input
          :disabled="disabled"
          :id="uid"
          :ref="uid"
          :placeholder="placeholder"
          :value="localValue"
          type="text"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <span class="unit" v-if="unit">{{ unit }}</span>

        <button @click="onClear" class="clear" type="button" v-if="showClear">
          <VIcon material="cancel" />
        </button>

        <button
          @click="increase"
          class="control-btn increase"
          type="button"
          v-if="showButtons"
        >
          <VIcon material="add" />
        </button>
      </div>

      <!-- Низ: сообщения (ошибки, ворнинги, успех, подсказки) -->
      <div class="messages-row" v-if="isErrorsVisible">
        <div class="error" v-if="validation.isValid === false">
          <div
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
            class="error-message"
          >
            {{ error }}
          </div>
        </div>

        <div class="warning" v-if="validation.warnings.length > 0">
          <div
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
            class="warning-message"
          >
            {{ warning }}
          </div>
        </div>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VInputNumberRus.scss" />

<script lang="ts">
import Unit from '@/domains/Units/Unit';
import Utils        from "@/core/utils/Utils";
import VBaseControl from "@/core/services/VBaseControl";
import Validation from "@/core/services/Validation";
import Constants from "@/core/misc/Constants";
import type { UnwrapNestedRefs } from 'vue';

export default defineComponent({
  name: 'VInputNumberRus',

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, label, layout, required, min, max,
    // minLength, maxLength, validateMethod, warningMethod, successMethod,
    // email, name, phone) — приходят из VBaseControl. Здесь только специфика.

    /** Показывать кнопку очистки. */
    clearable:   { type: Boolean,          default: null },
    /** Валидация по строке/цифре формата "10.3". Приоритет ниже, чем у min, max, precision. */
    range:       { type: [Number, String], default: null },
    /** Шаг изменения значения по кнопкам, кратность. */
    step:        { type: Number,           default: 1 },
    /** Показывать кнопки увеличения/уменьшения значения. */
    showButtons: { type: Boolean,          default: false },
    modelValue:  { type: [Number, String] as PropType<number | string | null>, default: null },
    value:       { type: [Number, String] as PropType<number | string | null>, default: null },
    /** Выравнивание текста в инпуте. Принимает значения `left, center, right`. */
    textAlign:   { type: String, default: null, validator: (value: string) => Constants.TEXT_ALIGNS.includes(value) },
    /** Текстовая подсказка, появляющаяся справа от контрола. */
    helperText:  { type: String, default: null, validator: (value: string) => typeof value === 'string' && value.length > 0 },
    /** Количество знаков после запятой. */
    precision:   { type: Number, default: 2,   validator: (value: number) => value >= 0 },
    /** Единица измерения справа от значения. Принимает строку ("кг", "шт", "$") или объект Unit. */
    unit:        { type: [String, Object] as PropType<string | null | Unit | UnwrapNestedRefs<Unit>>, default: null },
  },

  emits: ['update:modelValue', 'input', 'blur', 'focus'],

  data() {
    // VInputNumberRus имеет специфический проп range='10.3', который парсится
    // в min/max/precision поверх обычных пропов. VBaseControl этого не знает,
    // поэтому переопределяем поле validation в собственном data() —
    // Vue смерджит data() базы и наследника, наш ключ перебьёт базовый.
    //
    // Computed (getValidateRules) в data() недоступны — они инициализируются
    // после data(). Поэтому зовём метод parseRangeRules, который доступен,
    // потому что methods биндятся до data().
    const validation = Validation.fromProps(this as any);

    const rules = this.parseRangeRules();
    if (rules.min !== null) validation.min(rules.min);
    if (rules.max !== null) validation.max(rules.max);

    return {
      validation,
      localValue:         null,
      externalValueCache: null,
    };
  },

  computed: {
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true)   classes.push('clearable');
      if (this.showButtons === true) classes.push('show-buttons');
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this._convertValue(this.localValue) !== null
        && this.disabled === false;
    },

    // labelStyles VBaseControl возвращает { flex: '0 0 …' } — у конкретного инпута
    // исторически было { width: … }. Переопределяем под исходное поведение.
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) {
        result['width'] = `calc(100% - ${this.labelWidth})`;
      }
      if (this.textAlign !== null) {
        result['text-align'] = this.textAlign;
      }
      return result;
    },

    hasIconSlot(): boolean {
      return this.$slots['icon'] !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots['helper-text'] !== undefined;
    },

    getValidateRules() {
      return this.parseRangeRules();
    },

    getPrecision() {
      return this.parseRangeRules().precision;
    },
  },

  watch: {
    modelValue(newValue, oldValue) {
      this._handleExternalChange(newValue, oldValue);
    },
    value(newValue, oldValue) {
      this._handleExternalChange(newValue, oldValue);
    },
  },

  created() {
    this._syncLocalValue(true);
  },

  methods: {
    /**
     * Парсит проп range='10.3' в { min, max, precision }, накладывая обычные
     * пропы min/max поверх (они имеют приоритет). Используется и из data()
     * (где computed ещё не существуют), и из computed для шаблона.
     *
     * Формат range:
     *  - "10.3" или 10.3 → max = 9999999 (7 цифр), precision = 3
     *  - "-10.3" → симметричный диапазон [-9999999, 9999999]
     *  - "10"   → max по разрядности целых, без дробной части
     */
    parseRangeRules() {
      let { min, max, precision, range } = this;
      const result: { min: number | null, max: number | null, precision: number } = {
        min:       null,
        max:       null,
        precision,
      };
      let rangeMax, rangePrecision;
      let haveMinus = false;

      if (Utils.number.isNumber(range)) {
        let rangeStr = range.toString();
        if (rangeStr[0] === '-') {
          haveMinus = true;
          rangeStr = rangeStr.substr(1);
        }
        const [rangeMaxNumber, rangePrecisionNumber] = rangeStr
          .split('.')
          .map((item) => parseInt(item, 10));
        if (!isNaN(rangeMaxNumber) && !isNaN(rangePrecisionNumber) && rangeMaxNumber - rangePrecisionNumber >= 0) {
          rangeMax = rangeMaxNumber - rangePrecisionNumber;
        }
        if (!isNaN(rangePrecisionNumber)) {
          rangePrecision = rangePrecisionNumber;
        }
      }
      if (typeof range === 'string') {
        let rangeStr = range;
        if (rangeStr[0] === '-') {
          haveMinus = true;
          rangeStr = rangeStr.substr(1);
        }
        let [rangeMaxString, rangePrecisionString] = rangeStr.split('.');
        if (typeof rangeMaxString === 'string' && rangeMaxString.length > 0) {
          rangeMax = rangeMaxString.length;
        }
        if (typeof rangePrecisionString === 'string' && rangePrecisionString.length > 0) {
          rangePrecision = rangePrecisionString.length;
        }
      }
      if (Utils.number.isNumber(rangePrecision)) result.precision = rangePrecision;
      if (Utils.number.isNumber(rangeMax)) {
        result.max = 10 ** rangeMax - 1;
        result.min = haveMinus ? (10 ** rangeMax - 1) * -1 : 0;
      }
      if (Utils.number.isNumber(min)) result.min = min;
      if (Utils.number.isNumber(max)) result.max = max;
      return result;
    },

    onInput(event) {
      this.localValue = this._formatNumberString(event.target.value);
      event.target.value = this.localValue;
      const withPrecision = this._toPrecision(this.localValue);

      if (this.validation.isDirty === false) {
        const prevValue = this._getExternalValue();
        if (prevValue !== withPrecision) {
          this.validation.markDirty();
        }
      }

      this.externalValueCache = withPrecision;
      this._emitValue(withPrecision);
    },

    async onBlur(event) {
      // Если debounce уже запланировал валидацию — отменяем её,
      // потому что сейчас провалидируем синхронно по blur.
      if (typeof this.debounceValidation.cancel === 'function') {
        this.debounceValidation.cancel();
      }

      await this.validate();
      const withPrecision = this._toPrecision(event.target.value);
      this.localValue = this._convertToLocalValue(withPrecision);
      this.externalValueCache = withPrecision;
      event.target.value = withPrecision;
      this.$emit('blur', withPrecision);
    },

    onFocus(event) {
      this.validation.markTouched();
      this.$emit('focus', event.target);
    },

    isFocused() {
      return this.$refs[this.uid] === document.activeElement;
    },

    _emitValue(value) {
      this.$emit('update:modelValue', value);
      this.$emit('input', value);
    },

    _handleExternalChange(newValue, oldValue) {
      if (newValue === oldValue) return;
      this._syncLocalValue();
    },

    _syncLocalValue(force = false) {
      const externalValue = this._getExternalValue();
      if (force === false && externalValue === this.externalValueCache) return;
      this.externalValueCache = externalValue;
      this.localValue = this._convertToLocalValue(externalValue);
      // При внешнем изменении значения — отложенно валидируем,
      // чтобы серия v-model-присваиваний не дёргала validate на каждое.
      if (force === false) {
        this.debounceValidation();
      }
    },

    _getExternalValue() {
      if (this.modelValue !== null && this.modelValue !== undefined) {
        return this.modelValue;
      }
      if (this.value !== null && this.value !== undefined) {
        return this.value;
      }
      return null;
    },

    _convertToLocalValue(numberValue) {
      let stringValue = null;
      if (typeof numberValue === 'number') {
        stringValue = numberValue.toString();
      } else if (typeof numberValue === 'string') {
        stringValue = numberValue;
      }
      return this._formatNumberString(stringValue);
    },

    onClear() {
      this.localValue = null;
      this.externalValueCache = null;
      this._emitValue(null);
      this.$emit('blur', null);
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда вызывать валидацию,
     * чтобы не показывать ошибки до первого взаимодействия.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.localValue);
    },

    decrease() {
      let result = this._convertValue(this.localValue);
      if (result === null) result = 0;
      result = this.$core.utils.number.round10(result - this.step, this.getValidateRules.precision);
      if (this.min !== null && result <= this.min) {
        result = this.min;
      }
      this.localValue = this._convertToLocalValue(result);
      this.externalValueCache = result;
      this._emitValue(result);
    },

    increase() {
      let result = this._convertValue(this.localValue);
      if (result === null) result = 0;
      result = this.$core.utils.number.round10(result + this.step, this.getValidateRules.precision);
      if (this.max !== null && result >= this.max) {
        result = this.max;
      }
      this.localValue = this._convertToLocalValue(result);
      this.externalValueCache = result;
      this._emitValue(result);
    },

    /**
     * Нормализует введённую пользователем строку в валидный числовой ввод.
     * Принимает «грязный» текст из input (буквы, запятые, лишние точки, ведущие нули)
     * и возвращает строку, пригодную для дальнейшего parseFloat и отображения.
     *
     * Правила нормализации:
     *  - оставить только цифры, точку и запятую (всё остальное вырезать);
     *  - запятую заменить на точку (поддержка русской раскладки);
     *  - схлопнуть две точки в одну (вторую отбросить);
     *  - поправить ведущие случаи: ".5" → "0.5", "00" → "0" и т.д.;
     *  - обрезать дробную часть до this.getPrecision знаков;
     *  - обрезать целую часть, если она превышает разрядность this.max;
     *  - сохранить ведущий минус.
     */
    _formatNumberString(value) {
      // Результат. Останется null, если на вход пришло не-строка или пустая строка.
      let _value = null;

      // Работаем только со строками. Числа и null/undefined возвращаются как null.
      if (typeof value === 'string' && value.length > 0) {

        // --- Шаг 1: вынимаем ведущий минус, чтобы не мешал regex'ам и обработке нулей ---

        let minus = '';
        if (value[0] === '-') {
          minus = '-';
          // Дальше работаем с положительной частью строки.
          _value = value.slice(1);
        } else {
          _value = value;
        }

        // --- Шаг 2: удаляем всё, что не цифра, не точка и не запятая ---

        // Один replace убирает первую группу мусора. Дальше регэксп ниже проверит,
        // что строка целиком состоит из разрешённых символов — иначе дальше не идём.
        _value = _value.replace(/[^\d,.]+/, '');

        // Если в строке остались только разрешённые символы — продолжаем обработку.
        // Если remained пусто или содержит ещё мусор — выходим с тем, что есть.
        if (/^[\d,.]+$/.test(_value)) {

          // --- Шаг 3: запятая → точка (русская раскладка) ---

          _value = _value.replace(',', '.');

          // --- Шаг 4: если точек две — удаляем вторую ---

          // Пользователь мог быстро напечатать "1.2.3" — оставляем "1.23".
          const dotsArray = [..._value.matchAll(/\./g)];
          if (dotsArray.length === 2) {
            const _valueArray = _value.split('');
            // Берём индекс ВТОРОЙ точки и вырезаем именно её, не первую.
            _valueArray.splice(dotsArray[1].index, 1);
            _value = _valueArray.join('');
          }

          // --- Шаг 5: краевые случаи с первой точкой ---

          // Пользователь начал ввод с точки — нужно подставить ведущий ноль.
          if (_value[0] === '.') {
            switch (_value.length) {
              case 1:
                // Просто "." → "0." (предполагаем, что человек начинает дробь).
                _value = '0.';
                break;
              case 2:
                if (_value[1] === '0') {
                  // ".0" — бессмысленно как дробь, схлопываем до "0".
                  _value = '0';
                } else {
                  // ".5" → "0.5".
                  _value = `0${_value}`;
                }
                break;
              default:
                // ".25" → "0.25" и подобное.
                _value = `0${_value}`;
                break;
            }
          }
          // --- Шаг 6: краевые случаи с ведущим нулём ---
          else if (_value[0] === '0') {
            switch (_value.length) {
              case 1:
                // Просто "0" — оставляем как есть, это валидный ввод.
                break;
              case 2:
                // "05" → "5" (убираем ведущий ноль). "0." → "0." (оставляем).
                if (_value[1] !== '.') {
                  _value = _value.slice(1);
                }
                break;
              default:
                // "007" → "07" (убираем один ноль за раз — следующий ввод обработает дальше).
                // "0.5" — оставляем как есть.
                // Условие срабатывает, если второй символ — ноль (двойной ведущий ноль)
                // или если второй символ не точка (число вида "05...").
                if (_value[1] === '0' || _value[1] !== '.') {
                  _value = _value.slice(1);
                }
            }
          }

          // --- Шаг 7: обрезаем дробную часть до заданной точности ---

          if (this.getPrecision === 0) {
            // Целочисленный режим — дробь не нужна вообще.
            const _valueArray = _value.split('.');
            if (_valueArray.length > 1) {
              // "12.34" → "12".
              _value = _valueArray[0];
            }
          } else {
            // Дробный режим — обрезаем хвост до N знаков.
            const _valueArray = _value.split('.');
            if (_valueArray.length > 1) {
              _value = `${_valueArray[0]}.`;
              if (_valueArray[1].length > this.getPrecision) {
                // "12.34567" с precision=2 → "12.34".
                _value += _valueArray[1].slice(0, this.getPrecision);
              } else {
                // "12.3" остаётся "12.3" — короче лимита.
                _value += _valueArray[1];
              }
            }
          }
        }

        // --- Шаг 8: ограничиваем длину целой части по max ---

        // Если задан max — целая часть не должна быть шире его разрядности.
        // Например, max=999 → не больше 3 цифр до точки.
        if (this.max !== null) {
          const maxIntegerLength = Math.floor(Math.abs(this.max)).toString().length;
          const [intPart, decPart] = _value.split('.');
          if (intPart.length > maxIntegerLength) {
            // Обрезаем целую часть, дробную если есть — сохраняем.
            _value = decPart !== undefined
              ? intPart.slice(0, maxIntegerLength) + '.' + decPart
              : intPart.slice(0, maxIntegerLength);
          }
        }

        // --- Шаг 9: возвращаем минус на место ---

        _value = minus + _value;
      }

      return _value;
    },

    _convertValue(value) {
      switch (typeof value) {
        case 'number':
          return value;
        case 'string': {
          if (value.length === 0) {
            return null;
          }
          const _value = parseFloat(value);
          return isNaN(_value) ? null : _value;
        }
        default:
          return null;
      }
    },

    _toPrecision(number) {
      const temp = this._convertValue(number);
      if (temp === null) return null;
      if (this.getPrecision === 0) return Math.round(temp);
      const size = Math.pow(10, this.getPrecision);
      return Math.round(temp * size) / size;
    },
  },
});
</script>
